import { request } from '@/utils/http'
import { DOC_CONTROL_SYNC_TRIGGER_TYPE } from '@/constants/doc-control'

export interface DocControlDocumentVO {
  id?: string | number
  countryRegion?: string
  title?: string
  productCategory?: string
  certType?: string
  certProject?: string
  productModel?: string
  certOrg?: string
  effectiveDate?: string
  expiryDate?: string
  fileId?: string
  fileName?: string
  categoryKey?: string
  remark?: string
}

export interface DocControlPage<T> {
  list: T[]
  total: number
  pageNum: number
  pageSize: number
}

export interface DocControlSyncStatus {
  enabled?: boolean
  syncing?: boolean
  manualEnabled?: boolean
  lastSyncTime?: string
  lastSyncStatus?: string
  lastRecordCount?: number
  syncTimes?: string
}

export interface DocControlSyncLog {
  id?: number
  batchId?: string
  status?: string
  triggerType?: string
  recordCount?: number
  successCount?: number
  failCount?: number
  errorMessage?: string
  startedAt?: string
  finishedAt?: string
  dateCreated?: string
}

const BASE = '/api/v1/doc-control'

/**
 * 分页查询文控资料
 */
export function pageDocControlDocuments(params: {
  keyword?: string
  categoryKey?: string
  pageNum?: number
  pageSize?: number
}) {
  return request.get<DocControlPage<DocControlDocumentVO>>(`${BASE}/documents/page`, params)
}

/**
 * 批量下载：返回元数据（含 fileId），前端再调 universal 下载
 */
export function batchDownloadDocControlDocuments(ids: Array<string | number>) {
  return request.post<DocControlDocumentVO[]>(`${BASE}/documents/batch-download`, {
    ids: ids.map(id => String(id))
  })
}

/**
 * 查询文控同步状态
 */
export function getDocControlSyncStatus() {
  return request.get<DocControlSyncStatus>(`${BASE}/sync/status`)
}

/**
 * 查询文控同步配置
 */
export function getDocControlSyncConfig() {
  return request.get<DocControlSyncStatus>(`${BASE}/sync/config`)
}

/**
 * 更新文控同步配置
 */
export function updateDocControlSyncConfig(data: {
  enabled?: boolean
  syncTimes?: string
  manualEnabled?: boolean
}) {
  return request.put(`${BASE}/sync/config`, data)
}

/**
 * 手动触发文控同步
 */
export function triggerDocControlSync() {
  return request.post<{ batchId: string; status: string }>(`${BASE}/sync/trigger`, {
    triggerType: DOC_CONTROL_SYNC_TRIGGER_TYPE.MANUAL
  })
}

/**
 * 分页查询文控同步日志
 */
export function getDocControlSyncLogs(params: {
  status?: string
  pageNum?: number
  pageSize?: number
}) {
  return request.get<DocControlPage<DocControlSyncLog>>(`${BASE}/sync/logs`, params)
}

/**
 * 委托单导入文控报告
 */
export function importReportsToConsignment(
  orderId: string | number,
  documentIds: Array<string | number>,
  customerVisible = false
) {
  return request.post(`${BASE}/consignment/${orderId}/import-reports`, {
    documentIds: documentIds.map(id => String(id)),
    customerVisible
  })
}

/**
 * 查询委托单已导入的文控报告 ID
 */
export function listImportedReportIds(orderId: string | number) {
  return request.get<string[]>(`${BASE}/consignment/${orderId}/imported-report-ids`)
}

/**
 * 查询运输委托单文件列表（含文控导入 docControlDocumentId）
 */
export function listConsignmentFiles(orderId: string | number) {
  return request.post<Array<{
    id?: string | number
    fileId?: string
    fileName?: string
    fileNameDisplay?: string
    fileType?: string
    fileSize?: number
    uploadTime?: string
    uploadUserId?: string | number
    source?: string
    docControlDocumentId?: string | number
  }>>('/booking/order/file/list', { id: String(orderId) })
}

/**
 * 文控资料详情
 */
export function getDocControlDocument(id: string | number) {
  return request.get<DocControlDocumentVO>(`${BASE}/documents/${id}`)
}

/**
 * 文控主数据枚举
 */
export function listDocControlDict(type: string) {
  return request.get<Array<{ code: string; name: string; sortOrder?: number }>>(`${BASE}/dict/${type}`)
}

/**
 * 文控资料单文件下载 URL（浏览器直连或 axios blob）
 */
export function getDocControlDownloadUrl(id: string | number) {
  return `${BASE}/documents/${id}/download`
}
